import { useContext, useRef } from "react";
import type { OrderInventoryRestore } from "../../../api/orders";
import { OrderContext } from "../store/OrderContext";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { formatPhoneNumber } from "../utils/PhoneFormatters";
import { getFormattedCompanyRegNumber } from "../utils/getFormattedCompanyRegNumber";
import { ModalContext } from "../store/ModalContext";

import { getFormattedDate } from "../utils/getFormattedDate";
import ScrollableContainer from "../components/ScrollableContainer";
import "./OrdersPage.css";

function capitalizeProductName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function formatProductNameList(names: string[]): string {
  return names.map(capitalizeProductName).join(", ");
}

function getCanceledOrderModalBody(
  inventoryRestore: OrderInventoryRestore | undefined,
): string {
  const restored = inventoryRestore?.restored ?? [];
  const skipped = inventoryRestore?.skipped ?? [];

  if (restored.length === 0 && skipped.length === 0) {
    return "The order has been canceled.";
  }

  const parts: string[] = ["The order has been canceled."];

  if (restored.length > 0) {
    parts.push(
      `These products were automatically added back to harvest inventory: ${formatProductNameList(restored)}.`,
    );
  }

  if (skipped.length > 0) {
    parts.push(
      `These products are not in inventory and must be added manually: ${formatProductNameList(skipped)}.`,
    );
  }

  return parts.join(" ");
}

const OrderDetailsPage = () => {
  const { orders, updateStatus } = useContext(OrderContext);
  const { openModal, closeModal } = useContext(ModalContext);
  const { orderId: id } = useParams();
  const activeOrder = orders.find((order) => {
    return order.id === id;
  });

  const printableRef = useRef<HTMLDivElement>(null);

  const deliveryMethod = activeOrder?.deliveryMethod;
  const shippingPrice = activeOrder?.shippingPrice
    ? activeOrder?.shippingPrice
    : 0;

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: "Order Details",
  });

  if (!activeOrder) {
    return <p>Oops! Looks like no order exists with this orderID.</p>;
  }

  const date = getFormattedDate(activeOrder.createdAt);
  const isPending = activeOrder.status === "Pending";
  const isDone = activeOrder.status === "Done";
  const isCanceled = activeOrder.status === "Canceled";

  const products =
    activeOrder.products.map((product) => (
      <tr key={product.productName}>
        <td className="td capitalized">{product.productName}</td>
        <td className="td capitalized">{product.quantity}</td>
        <td className="td capitalized">{product.totalPrice}</td>
      </tr>
    )) ?? null;

  function onOpenWarningModal(orderId: string, status: string) {
    const statusLabel =
      status === "Done"
        ? "Finished"
        : status === "Canceled"
          ? "Canceled"
          : "Pending";
    if (orderId) {
      openModal({
        title: "WARNING",
        body: `Are you sure you want to change the status to "${statusLabel}"?`,
        buttons: [
          { text: "No", callback: closeModal },
          {
            text: "Yes",
            loadingText: "Updating...",
            callback: () => handleUpdateStatus(orderId, status),
          },
        ],
      });
    }
  }

  async function handleUpdateStatus(orderId: string, status: string) {
    try {
      const updated = await updateStatus(orderId, status);
      closeModal();
      if (status === "Canceled") {
        openModal({
          title: "Order canceled",
          body: <p>{getCanceledOrderModalBody(updated.inventoryRestore)}</p>,
          showCloseButton: true,
        });
      }
    } catch {
      closeModal();
      openModal({
        title: "WARNING",
        body: "Failed to update order status. Please try again.",
        showCloseButton: true,
      });
    }
  }

  return (
    <div className="page-container">
      <h1>Order Details</h1>
      <div className="btn-aligned-right-box">
        <button className="btn btn-aligned-right" onClick={handlePrint}>
          <span>🖨️</span> Print Or Save
        </button>
      </div>
      <div className="page-content">
        <div ref={printableRef} className="print">
          <div className="order-sibling-layout">
            <div className="print-header order-less-margin">
              <h2>
                Order: <span className="id-cell">{id}</span>
              </h2>
              <h3>{activeOrder.businessName}</h3>
            </div>
            <div className="list-of-buttons">
              {(isPending || isDone) && (
                <button
                  className={
                    isPending ? "btn" : "btn disabled-btn order-action-done"
                  }
                  onClick={() => onOpenWarningModal(activeOrder.id, "Done")}
                  disabled={!isPending}
                >
                  {isPending ? "Mark As Finished" : "Finished"}
                </button>
              )}
              {(isPending || isCanceled) && (
                <button
                  className={
                    isPending ? "btn" : "btn disabled-btn order-action-canceled"
                  }
                  onClick={() => onOpenWarningModal(activeOrder.id, "Canceled")}
                  disabled={!isPending}
                >
                  {isPending ? "Cancel Order" : "Canceled"}
                </button>
              )}
            </div>
          </div>
          <div className="info-card order-info-structure order-info-card-extra-margin">
            <div className="order-details-info-bubble">
              {" "}
              <h4 className="h4-less-margin-bottom">Company Reg Number:</h4>
              <p className="order-detail-p">
                {getFormattedCompanyRegNumber(
                  activeOrder.companyRegistrationNumber,
                )}
              </p>
              <h4 className="h4-less-margin-bottom"> Phone Number:</h4>
              <p className="order-detail-p">
                {formatPhoneNumber(activeOrder.phone)}
              </p>{" "}
            </div>
            <div className="order-details-info-bubble">
              <h4 className="h4-less-margin-bottom">Time of Order:</h4>
              <p className="order-detail-p">{date}</p>

              {deliveryMethod === "shipping" && (
                <div>
                  <h4 className="h4-less-margin-bottom">Shipping Address:</h4>
                  <p className="order-detail-p">
                    {activeOrder.companyAddress} <br />{" "}
                    {activeOrder.companyPostalCode}, {activeOrder.companyCity}
                  </p>{" "}
                </div>
              )}
            </div>{" "}
          </div>

          <div className="order-details-container">
            <ScrollableContainer>
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Product</th>
                    <th className="th">Quantity</th>
                    <th className="th">Sum</th>
                  </tr>
                </thead>
                <tbody>{products}</tbody>
                <tfoot>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      Subtotal: {activeOrder.totalPrice} kr
                    </td>
                  </tr>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      {deliveryMethod === "shipping"
                        ? `Delivery: ${shippingPrice} kr`
                        : "Pickup at Alnarp's Farm."}
                    </td>
                  </tr>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      Total: {activeOrder.totalPrice + shippingPrice} kr
                    </td>
                  </tr>
                </tfoot>
              </table>
              <table className="table print-footer">
                <tfoot>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      Subtotal: {activeOrder.totalPrice} kr
                    </td>
                  </tr>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      {deliveryMethod === "shipping"
                        ? `Delivery: ${shippingPrice} kr`
                        : "Pickup at Alnarp's Farm."}
                    </td>
                  </tr>
                  <tr>
                    <td className="td td-left" colSpan={3}>
                      Total: {activeOrder.totalPrice + shippingPrice} kr
                    </td>
                  </tr>
                </tfoot>
              </table>
            </ScrollableContainer>
          </div>
          {isCanceled && (
            <div className="canceled-order-status-tag">
              <h4 className="h4-less-margin-bottom white">Canceled</h4>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
